import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { PublicClientApplication, EventType, Configuration } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { initializeIcons } from '@fluentui/react';
import { msalConfig } from './auth/msalConfig';
import { setMsalInstance, setApiScopes } from './auth/tokenHelper';
import { Theme } from './theme';
import App from './App';
import './index.css';

initializeIcons();
Theme.get();

async function getAuthConfig(): Promise<{ config: Configuration; clientId: string }> {
  try {
    const apiBase = import.meta.env.VITE_API_URL || '';
    const res = await fetch(`${apiBase}/api/config`);
    if (res.ok) {
      const { clientId, tenantId } = await res.json();
      return {
        clientId,
        config: {
          ...msalConfig,
          auth: {
            ...msalConfig.auth,
            clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
          },
        },
      };
    }
  } catch {
    // Fall through to env-var config
  }
  return { config: msalConfig, clientId: import.meta.env.VITE_AZURE_CLIENT_ID };
}

getAuthConfig().then(async ({ config, clientId }) => {
  setApiScopes([`api://${clientId}/access_as_user`]);

  const msalInstance = new PublicClientApplication(config);
  await msalInstance.initialize();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const account = (event.payload as any).account;
      msalInstance.setActiveAccount(account);
    }
  });

  setMsalInstance(msalInstance);

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>,
  );
});
